import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api';
import { ScoreTrendChart } from '../components/rehearsal';

interface RehearsalSession {
  id: string;
  userId: string;
  difficulty: 'easy' | 'medium' | 'hard';
  questionFocus: string[];
  status: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  overallScore: number | null;
  questionsTotal: number;
  questionsAnswered: number;
  questionsSkipped: number;
}

interface RehearsalStats {
  totalSessions: number;
  totalQuestions: number;
  averageScore: number | null;
  scoresByType: Record<string, number>;
  scoresByDifficulty: Record<string, number>;
  recentScores: Array<{
    sessionId: string;
    score: number;
    date: number;
    difficulty: string;
  }>;
}

interface RehearsalsResponse {
  rehearsals: RehearsalSession[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const DIFFICULTY_COLORS = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-orange-100 text-orange-700',
  hard: 'bg-red-100 text-red-700',
};

const SCORE_COLORS = {
  excellent: 'text-green-500',
  good: 'text-blue-500',
  acceptable: 'text-yellow-500',
  needsWork: 'text-orange-500',
  missed: 'text-red-500',
};

function getScoreColor(score: number | null): string {
  if (score === null) return 'text-slate-400';
  if (score >= 85) return SCORE_COLORS.excellent;
  if (score >= 70) return SCORE_COLORS.good;
  if (score >= 50) return SCORE_COLORS.acceptable;
  if (score >= 30) return SCORE_COLORS.needsWork;
  return SCORE_COLORS.missed;
}

function getScoreLabel(score: number | null): string {
  if (score === null) return 'No Score';
  if (score >= 85) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Acceptable';
  if (score >= 30) return 'Needs Work';
  return 'Needs Practice';
}

export function RehearsalHistory() {
  const { get, delete: del } = useApi();
  const [rehearsals, setRehearsals] = useState<RehearsalSession[]>([]);
  const [stats, setStats] = useState<RehearsalStats | null>(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function fetchRehearsals(page = 1) {
    try {
      setLoading(true);
      const data = await get<RehearsalsResponse>(`/rehearsals?page=${page}&limit=20`);
      setRehearsals(data.rehearsals);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rehearsals');
    } finally {
      setLoading(false);
    }
  }

  async function fetchStats() {
    try {
      const data = await get<{ stats: RehearsalStats }>('/users/me/rehearsal-stats');
      setStats(data.stats);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }

  useEffect(() => {
    fetchRehearsals();
    fetchStats();
  }, []);

  async function handleDelete(rehearsalId: string) {
    if (!confirm('Are you sure you want to delete this rehearsal?')) return;
    try {
      await del(`/rehearsals/${rehearsalId}`);
      setRehearsals((prev) => prev.filter((r) => r.id !== rehearsalId));
    } catch (err) {
      alert('Failed to delete rehearsal');
    }
  }

  function formatDuration(ms: number | null): string {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  }

  if (loading && rehearsals.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Rehearsal History</h1>
          <p className="mt-1 text-slate-400">
            {pagination.total} total rehearsals
          </p>
        </div>
        <Link
          to="/app"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
        >
          New Rehearsal
        </Link>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
            <div className="text-sm text-slate-400">Total Sessions</div>
            <div className="text-2xl font-bold text-white">{stats.totalSessions}</div>
          </div>
          <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
            <div className="text-sm text-slate-400">Questions Answered</div>
            <div className="text-2xl font-bold text-white">{stats.totalQuestions}</div>
          </div>
          <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
            <div className="text-sm text-slate-400">Average Score</div>
            <div className={`text-2xl font-bold ${getScoreColor(stats.averageScore)}`}>
              {stats.averageScore !== null ? `${Math.round(stats.averageScore)}%` : '-'}
            </div>
          </div>
          <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
            <div className="text-sm text-slate-400">Performance</div>
            <div className={`text-2xl font-bold ${getScoreColor(stats.averageScore)}`}>
              {getScoreLabel(stats.averageScore)}
            </div>
          </div>
        </div>
      )}

      {/* Score Trend Chart */}
      {stats && stats.recentScores && stats.recentScores.length > 0 && (
        <ScoreTrendChart scores={stats.recentScores} height={140} />
      )}

      {/* Score by Difficulty */}
      {stats && stats.scoresByDifficulty && Object.keys(stats.scoresByDifficulty).length > 0 && (
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <h3 className="text-sm font-medium text-slate-400 mb-3">Scores by Difficulty</h3>
          <div className="flex gap-4 flex-wrap">
            {Object.entries(stats.scoresByDifficulty).map(([difficulty, score]) => (
              <div key={difficulty} className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  DIFFICULTY_COLORS[difficulty as keyof typeof DIFFICULTY_COLORS] || 'bg-slate-700 text-slate-300'
                }`}>
                  {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                </span>
                <span className={`font-bold ${getScoreColor(score)}`}>
                  {Math.round(score)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rehearsals list */}
      {rehearsals.length > 0 ? (
        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Difficulty
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Questions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {rehearsals.map((rehearsal) => {
                const date = new Date(rehearsal.startedAt);
                const score = Number(rehearsal.overallScore ?? 0);
                const scoreColor = getScoreColor(rehearsal.overallScore);
                const difficultyColor = DIFFICULTY_COLORS[rehearsal.difficulty] || DIFFICULTY_COLORS.easy;

                return (
                  <tr key={rehearsal.id} className="hover:bg-slate-700/50">
                    <td className="px-6 py-4 text-slate-300">
                      {date.toLocaleDateString()}
                      <br />
                      <span className="text-sm text-slate-500">
                        {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${difficultyColor}`}>
                        {rehearsal.difficulty.charAt(0).toUpperCase() + rehearsal.difficulty.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-300">
                      {formatDuration(rehearsal.durationMs)}
                    </td>
                    <td className="px-6 py-4 text-slate-300">
                      <div className="flex flex-col">
                        <span>{rehearsal.questionsAnswered}/{rehearsal.questionsTotal} answered</span>
                        {rehearsal.questionsSkipped > 0 && (
                          <span className="text-xs text-slate-500">
                            {rehearsal.questionsSkipped} skipped
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {rehearsal.overallScore !== null ? (
                        <div className="flex flex-col">
                          <span className={`text-lg font-bold ${scoreColor}`}>
                            {Math.round(score)}%
                          </span>
                          <span className={`text-xs ${scoreColor}`}>
                            {getScoreLabel(rehearsal.overallScore)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          rehearsal.status === 'completed'
                            ? 'bg-green-900/50 text-green-400'
                            : rehearsal.status === 'active'
                            ? 'bg-blue-900/50 text-blue-400'
                            : 'bg-slate-700 text-slate-400'
                        }`}
                      >
                        {rehearsal.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDelete(rehearsal.id)}
                        className="text-slate-400 hover:text-red-400"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-slate-800 rounded-lg p-8 text-center border border-slate-700">
          <p className="text-slate-400">No rehearsals found</p>
          <p className="text-sm text-slate-500 mt-1">
            Start a rehearsal session to practice your pitch and track your progress.
          </p>
          <Link
            to="/app"
            className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
          >
            Start your first rehearsal
          </Link>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => fetchRehearsals(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="px-4 py-2 bg-slate-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-slate-400">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => fetchRehearsals(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            className="px-4 py-2 bg-slate-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
